import { Component, OnInit, Input, OnDestroy, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { KpiService } from "../../kpi/kpi.service";
import {
  keys,
  last,
  map,
  keyBy,
  get,
  omit,
  uniq,
  has,
  filter,
  compact,
  head,
} from "lodash";
import { OOPS_MESSAGES } from "../../constants";
import { StudentCreditsEarned } from "../../shared/interfaces";

@Component({
  selector: "expand-tile",
  templateUrl: "./expand-tile.component.html",
  styleUrls: ["./expand-tile.component.scss"],
})
export class ExpandTileComponent implements OnInit, OnDestroy {
  @Input() metadata;
  @Input() studentFirstName;
  @Input() entityId;
  @Input() custId;
  @Input()
  schoolMinGradeLevel;
  @ViewChild("ExpandTileTable") expandTileTable;
  loading: boolean;
  studentKPIDataSub: Subscription;
  data;
  tableData;
  displayedColumns: Array<string>;
  tableVisible: boolean;
  gradeYears: Array<string>;
  selectedGradeYear: string;
  yearTables;
  loadingTable: boolean;
  studentKpiId: number;
  studentGradesEnabled: boolean;
  hasOverallChartData: boolean;
  creditsEarnedEnabled: boolean;
  graduationPlanName: string;
  periodOrder: Array<string>;

  constructor(public kpiService: KpiService) {}

  ngOnInit() {
    this.studentKpiId =
      has(this.metadata, "studentKPIs") && this.metadata.studentKPIs[0]
        ? this.metadata.studentKPIs[0].studentKPI
        : null;
    this.getChartsForTile(this.metadata, this.entityId);
  }

  ngOnDestroy(): void {
    if (this.studentKPIDataSub) {
      this.studentKPIDataSub.unsubscribe();
    }
  }

  getChartsForTile(kpi, entityId = null) {
    this.loading = true;
    this.hasOverallChartData = false;
    if (kpi.studentKPIs && kpi.studentKPIs.length > 0) {
      this.studentKPIDataSub = this.kpiService
        .getStudentKpiData(entityId, kpi.studentKPIs[0].studentKPI)
        .subscribe(
          (data) => {
            this.data = data;
            this.loading = false;
            this.hasOverallChartData =
              this.data &&
              this.data.charts &&
              this.data.charts.overall &&
              this.data.charts.overall.chartData;
            this.studentGradesEnabled =
              this.hasOverallChartData && this.data.charts.studentGrades;
            this.creditsEarnedEnabled =
              this.hasOverallChartData && this.data.charts.studentCreditsEarned;

            if (this.studentGradesEnabled && this.data?.charts?.trend?.categories) {
              this.periodOrder = this.data.charts.trend.categories
            }
          },
          (error) => {
            this.loading = false;
            this.handleError(error);
          }
        );
    } else {
      // we don't have any student kpis
      this.loading = false;
    }
  }

  openPanel() {
    // emit "show more" click event
    this.kpiService
      .trackStudentProfileExpandPanelSelect(this.studentKpiId)
      .subscribe(
        (data) => {},
        (error) => {
          console.error(error);
        }
      );
    if (this.studentGradesEnabled) {
      this.getStudentGrades();
    } else {
      this.getCreditsEarned();
    }
  }

  getCreditsEarned() {
    this.loadingTable = true;
    this.kpiService
      .getStudentCreditsEarned(
        this.metadata.studentKPIs[0].studentKPI,
        this.entityId
      )
      .subscribe(
        (studentCreditsEarned: StudentCreditsEarned) => {
          this.loadingTable = false;
          if (studentCreditsEarned) {
            this.graduationPlanName = studentCreditsEarned.graduationPlanName;
            this.tableData = this.formatTableData(studentCreditsEarned.data);
            let tableRow = last(this.tableData);
            // only display columns that contain at least one value
            this.displayedColumns = keys(tableRow).filter((key) => {
              return this.checkForNonEmptyArray(uniq(map(this.tableData, key)));
            });
          }
        },
        (error) => {
          this.loadingTable = false;
          this.handleError(error);
        }
      );
  }

  getStudentGrades() {
    if (!this.gradeYears) {
      this.loadingTable = true;
      this.kpiService
        .getStudentGrades(
          this.metadata.studentKPIs[0].studentKPI,
          this.entityId
        )
        .subscribe(
          (data) => {
            this.loadingTable = false;
            if (data) {
              this.setYears(data);
            }
          },
          (error) => {
            this.loadingTable = false;
            this.handleError(error);
          }
        );
    }
  }

  setYears(data) {
    if (this.schoolMinGradeLevel) {
      data = filter(data, (item) => {
        return item.grade >= this.schoolMinGradeLevel - 1;
      });
    }
    this.gradeYears = map(data, "year");
    this.yearTables = keyBy(data, "year");
    if (this.gradeYears && this.gradeYears.length > 0) {
      this.selectGradeYear(last(this.gradeYears));
    }
  }

  formatTableData(tableData) {
    let formattedData;
    if (this.studentGradesEnabled) {
      formattedData = map(tableData, (row: any) => {
        let course = get(row, "CourseName");
        let period = get(row, "ClassPeriod");
        let coreCourse = get(row, "IsCoreCourse");
        let grades = omit(row, ["CourseName", "ClassPeriod", "IsCoreCourse"]);

        return {
          Course: course,
          Period: period,
          "Core Course": coreCourse,
          ...grades,
        };
      });
    } else if (this.creditsEarnedEnabled) {
      formattedData = map(tableData, (row) => {
        let creditArea = get(row, "CreditArea");
        let cumulativeCreditsEarned = get(row, "CumulativeCreditsEarned");
        let totalRequired = get(row, "TotalRequired");

        return {
          "Credit Area": creditArea,
          "Total Required": totalRequired,
          "Cumulative Credits Earned": cumulativeCreditsEarned,
        };
      });
    }
    return formattedData;
  }

  selectGradeYear(gradeYear, fromUserClick = false) {
    this.expandTileTable.clearTableSort(); // clear sort
    this.selectedGradeYear = gradeYear;
    let currentYear = get(this.yearTables, gradeYear);
    this.tableData = this.formatTableData(currentYear["data"]);
    let tableRow = last(this.tableData);

    // start with a fixed column order that matches the line chart displayed above the table
    this.displayedColumns = ["Course", "Period", "Core Course"]
    if (this.periodOrder) {
      this.displayedColumns = this.displayedColumns.concat(this.periodOrder)
    }

    // find all columns from the table that have some data in them
    let visibleColumns = keys(tableRow).filter((key) => {
      return this.checkForNonEmptyArray(uniq(map(this.tableData, key)));
    });

    // if the data somehow includes columns that aren't in the corresponding line chart, add them on at the end (in a random order)
    for (let column in visibleColumns) {
      if (!this.displayedColumns.includes(column)) {
        this.displayedColumns.push(column);
      }
    }

    // if a column has no data, remove it
    this.displayedColumns = this.displayedColumns.filter(item => visibleColumns.includes(item) )

    if (fromUserClick) {
      // "table time period toggled" click event
      this.kpiService.trackStudentProfileTableTimePeriodSelect().subscribe(
        (data) => {},
        (error) => {
          console.error(error);
        }
      );
    }
  }

  checkForNonEmptyArray(arr) {
    return (arr && arr.length > 1) || (arr.length == 1 && arr[0] != null);
  }

  handleError(error) {
    this.kpiService.showError(
      error,
      OOPS_MESSAGES.ACCESS_CONTENT_VIEW,
      OOPS_MESSAGES.RENDER_VIEW
    );
  }

  panelCollapsed() {
    // "show less" click event
    this.kpiService
      .trackStudentProfileCollapsePanelSelect(this.studentKpiId)
      .subscribe(
        (data) => {},
        (error) => {
          console.error(error);
        }
      );
    this.gradeYears = null;
  }
}
